import { FC } from 'react';
import { useKeyboardShortcut } from 'src/hooks';
import { LearningSessionError, SessionType } from 'src/api/learningSessions/types';
import { TestLearnableCard } from '../learnableCards';
import { BaseSession } from '../BaseSession';
import {
  LearningSessionAction,
  LearningSessionCardState,
} from '../../hooks/learningSessionReducer';
import SessionExtras from './SessionExtras';
import BehindCountdown from './BehindCountdown';
import { NumLives } from './SessionExtras/Lives';

export const INTRO_TIMER_DURATION_IN_MS = 3000;
export const NUM_LIVES = 3;

export interface SpeedReviewSessionProps {
  error: LearningSessionError | null;
  numCorrectAnswers: number;
  numIncorrectAnswers: number;
  onFeedbackFinished: () => void;
  onIntroCountdownFinished: () => void;
  percentageTestTimeLeft: number;
  sendCurrentLearningProgress: () => Promise<void>;
  title: string;
  card: NonNullable<LearningSessionCardState['card']>;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  analyticsSessionId?: string;
}

export const SpeedReview: FC<SpeedReviewSessionProps> = ({
  error,
  numCorrectAnswers,
  numIncorrectAnswers,
  onFeedbackFinished,
  onIntroCountdownFinished,
  percentageTestTimeLeft,
  sendCurrentLearningProgress,
  title,
  card,
  learningSessionDispatch,
  analyticsSessionId,
}) => {
  useKeyboardShortcut('enter', onFeedbackFinished);

  /* istanbul ignore next: typecheck only */
  if (card.data.kind !== 'Test') throw new Error('you need to pass a test card to speed review');

  return (
    <BaseSession
      title={title}
      error={error}
      loading={false}
      sendCurrentLearningProgress={sendCurrentLearningProgress}
      sessionType={SessionType.SPEED_REVIEW}
      analyticsSessionId={analyticsSessionId}
    >
      <BehindCountdown
        onCountdownFinished={onIntroCountdownFinished}
        durationInMs={INTRO_TIMER_DURATION_IN_MS}
      >
        <>
          <SessionExtras
            numLives={(NUM_LIVES - numIncorrectAnswers) as NumLives}
            percentageTimeLeft={percentageTestTimeLeft}
            correctAnswers={numCorrectAnswers}
          />
          <TestLearnableCard
            showInstruction={false}
            prompt={card.data.prompt}
            response={card.data.response}
            attributeValues={card.data.attributeValues}
            responseState={card.responseState}
            userAnswer={card.userAnswer}
            learningSessionDispatch={learningSessionDispatch}
          />
        </>
      </BehindCountdown>
    </BaseSession>
  );
};
